import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Sparkle, PaperPlaneRight, CircleNotch, ChatCircleDots, MagnifyingGlass, ChartBar, User, Gauge, Cpu, Check,
} from "@phosphor-icons/react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, Loading } from "@/components/riq";
import { shortDate } from "@/lib/format";
import { useAdminNav } from "@/pages/adminNav";

const SUGGESTED = [
  "How many users joined in the last 7 days?",
  "What are the top merchants by spend?",
  "What is the total spend captured across the platform?",
  "Which product categories are most popular?",
  "How is receipt volume trending this month?",
  "What is our OCR success rate?",
];

const CAT_COLORS = {
  "Spending totals": "bg-riq-primary/10 text-riq-primary",
  "Warranty": "bg-amber-100 text-amber-700",
  "Budget": "bg-teal-100 text-teal-700",
  "Prices & savings": "bg-emerald-100 text-emerald-700",
  "Merchant lookup": "bg-indigo-100 text-indigo-700",
  "Category breakdown": "bg-sky-100 text-sky-700",
  "Items & products": "bg-fuchsia-100 text-fuchsia-700",
  "Trends & time": "bg-rose-100 text-rose-700",
  "Other": "bg-neutral-100 text-neutral-600",
};

export default function AdminAI() {
  const { openUser } = useAdminNav();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef();

  const [insights, setInsights] = useState(null);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [usage, setUsage] = useState(null);
  const [ocr, setOcr] = useState(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);
  useEffect(() => { const t = setTimeout(() => setDebounced(search.trim()), 350); return () => clearTimeout(t); }, [search]);
  useEffect(() => {
    const qs = debounced ? `?q=${encodeURIComponent(debounced)}` : "";
    api.get(`/admin/ai/questions${qs}`).then((r) => setInsights(r.data)).catch(() => setInsights(false));
  }, [debounced]);
  useEffect(() => { api.get("/admin/llm-usage").then((r) => setUsage(r.data)).catch(() => setUsage(false)); }, []);
  useEffect(() => { api.get("/admin/ocr-settings").then((r) => setOcr(r.data)).catch(() => setOcr(false)); }, []);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setLoading(true);
    try {
      const { data } = await api.post("/admin/ai/query", { question: q });
      setMessages((m) => [...m, { role: "ai", text: data.answer, chart: data.chart }]);
    } catch {
      setMessages((m) => [...m, { role: "ai", text: "Sorry, I couldn't answer that right now. Please try again." }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-ai-section">
      {ocr && ocr !== false && <ScanEngineCard ocr={ocr} onSaved={setOcr} />}
      {usage && usage !== false && <UsageCard usage={usage} />}
      {/* Ask AI — platform analytics assistant */}
      <div>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-2xl bg-riq-primary flex items-center justify-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-20 riq-noise" />
            <Sparkle size={22} weight="fill" className="text-riq-gold relative" />
          </div>
          <div>
            <h2 className="font-head text-xl font-bold tracking-tight text-riq-primary">Ask AI</h2>
            <p className="text-sm text-muted-foreground">Ask about platform-wide data — users, spend, merchants, trends.</p>
          </div>
        </div>

        <Card className="p-4 sm:p-5">
          <div className="max-h-[420px] overflow-y-auto space-y-4">
            {messages.length === 0 && (
              <div className="py-2">
                <div className="rounded-2xl bg-secondary/60 border border-riq-border p-4 mb-4">
                  <p className="text-sm text-neutral-700 leading-relaxed">
                    I answer from live, aggregated data across <b>all users</b> — no individual receipts. Try one of these:
                  </p>
                </div>
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {SUGGESTED.map((s) => (
                    <button key={s} data-testid={`admin-ai-suggested-${s.slice(0, 10)}`} onClick={() => send(s)}
                      className="text-left rounded-xl border border-riq-border bg-white px-4 py-3 text-sm hover:border-riq-primary hover:-translate-y-0.5 transition-all">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={m.role === "user" ? "flex justify-end" : ""}>
                {m.role === "user" ? (
                  <div data-testid={`admin-ai-user-msg-${i}`} className="bg-riq-primary text-white rounded-2xl rounded-br-md px-4 py-3 text-sm max-w-[80%]">{m.text}</div>
                ) : (
                  <div className="max-w-[92%]">
                    <div className="flex items-center gap-2 mb-1.5 text-xs text-muted-foreground"><Sparkle size={14} weight="fill" className="text-riq-gold" /> Sliply AI</div>
                    {m.chart?.data?.length > 0 && <AIChart chart={m.chart} idx={i} />}
                    <div data-testid={`admin-ai-msg-${i}`} className="bg-secondary rounded-2xl rounded-tl-md px-4 py-3 text-sm text-neutral-800 leading-relaxed whitespace-pre-wrap">{m.text}</div>
                  </div>
                )}
              </motion.div>
            ))}

            {loading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><CircleNotch size={16} className="animate-spin" /> Crunching the platform numbers…</div>
            )}
            <div ref={endRef} />
          </div>

          <div className="pt-3 mt-3 border-t border-riq-border">
            <div className="flex items-center gap-2 bg-white rounded-full border border-riq-border pl-5 pr-2 py-2 focus-within:border-riq-primary">
              <input data-testid="admin-ai-input" value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask about the platform…"
                className="flex-1 bg-transparent text-sm outline-none" />
              <button data-testid="admin-ai-send-btn" onClick={() => send()} disabled={loading || !input.trim()}
                className="w-10 h-10 rounded-full bg-riq-primary text-white flex items-center justify-center disabled:opacity-50 active:scale-95 transition-transform">
                <PaperPlaneRight size={18} weight="fill" />
              </button>
            </div>
          </div>
        </Card>
      </div>

      {/* What users are asking */}
      <div data-testid="admin-ai-questions">
        <div className="flex items-center gap-2 mb-3">
          <ChatCircleDots size={20} weight="duotone" className="text-riq-primary" />
          <h3 className="font-head font-semibold text-riq-primary">What users are asking</h3>
          {insights?.total != null && (
            <span className="rounded-full bg-secondary text-neutral-500 px-2.5 py-0.5 text-xs font-semibold">{insights.total} total</span>
          )}
        </div>

        {insights === null ? (
          <Loading label="Loading question insights…" />
        ) : insights === false ? (
          <div className="text-muted-foreground text-sm">Could not load question insights.</div>
        ) : (
          <div className="space-y-5">
            {/* Category breakdown */}
            <Card>
              <div className="flex items-center gap-2 mb-3">
                <ChartBar size={17} weight="duotone" className="text-riq-primary" />
                <h4 className="font-head font-semibold text-sm text-riq-primary">Popular topics</h4>
              </div>
              {insights.categories.length === 0 ? (
                <p className="text-sm text-muted-foreground">No questions asked yet.</p>
              ) : (
                <div className="space-y-2.5">
                  {insights.categories.map((c) => {
                    const max = insights.categories[0]?.count || 1;
                    return (
                      <div key={c.category} data-testid={`ai-cat-${c.category}`} className="flex items-center gap-3">
                        <span className={`text-xs font-semibold rounded-full px-2.5 py-1 shrink-0 w-40 truncate ${CAT_COLORS[c.category] || CAT_COLORS.Other}`}>{c.category}</span>
                        <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
                          <div className="h-full rounded-full bg-riq-primary" style={{ width: `${Math.max(6, (c.count / max) * 100)}%` }} />
                        </div>
                        <span className="text-sm font-head font-bold text-riq-primary w-8 text-right shrink-0">{c.count}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            {/* Searchable feed */}
            <div>
              <div className="relative mb-3">
                <MagnifyingGlass size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input data-testid="admin-ai-search" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search questions…"
                  className="w-full rounded-full border border-riq-border pl-10 pr-4 py-2.5 text-sm outline-none focus:border-riq-primary" />
              </div>
              <Card className="p-0">
                {insights.questions.length === 0 ? (
                  <div className="py-10 text-center text-sm text-muted-foreground">No questions match “{debounced}”.</div>
                ) : (
                  <div className="divide-y divide-riq-border">
                    {insights.questions.map((q, i) => (
                      <div key={i} data-testid={`ai-question-row-${i}`} className="flex items-start gap-3 px-5 py-3.5">
                        <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 shrink-0 mt-0.5 ${CAT_COLORS[q.category] || CAT_COLORS.Other}`}>{q.category}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-neutral-800">{q.question}</div>
                          <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
                            <button data-testid={`ai-question-user-${i}`} onClick={() => q.user_id && openUser(q.user_id)}
                              className="text-riq-primary font-medium hover:underline inline-flex items-center gap-1">
                              <User size={12} weight="duotone" /> {q.user_name || q.user_email || "Unknown user"}
                            </button>
                            {" · "}{shortDate(q.at)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ScanEngineCard({ ocr, onSaved }) {
  const [engine, setEngine] = useState(ocr.primary_engine);
  const [model, setModel] = useState(ocr.gemini_model);
  const [saving, setSaving] = useState(false);
  const dirty = engine !== ocr.primary_engine || model !== ocr.gemini_model;
  const noGemini = !ocr.gemini_available;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/ocr-settings", { primary_engine: engine, gemini_model: model });
      onSaved({ ...ocr, primary_engine: data.primary_engine, gemini_model: data.gemini_model });
      setEngine(data.primary_engine); setModel(data.gemini_model);
      toast.success("Scan engine settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save settings");
    } finally { setSaving(false); }
  };

  const EngineBtn = ({ id, title, sub, disabled }) => (
    <button
      data-testid={`scan-engine-${id}`}
      disabled={disabled}
      onClick={() => setEngine(id)}
      className={`flex-1 text-left rounded-xl border-2 px-4 py-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${engine === id ? "border-riq-primary bg-riq-primary/5" : "border-riq-border hover:border-riq-primary/40"}`}
    >
      <div className="flex items-center justify-between">
        <span className="font-head font-semibold text-sm text-riq-primary">{title}</span>
        {engine === id && <Check size={16} weight="bold" className="text-riq-primary" />}
      </div>
      <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>
    </button>
  );

  return (
    <Card data-testid="admin-scan-engine">
      <div className="flex items-center gap-2 mb-1"><Cpu size={20} weight="duotone" className="text-riq-primary" /><h3 className="font-head font-semibold text-riq-primary">Scan engine</h3></div>
      <p className="text-sm text-muted-foreground mb-4">Choose which engine reads receipts first. The other one automatically backs it up, with free local OCR as a last resort.</p>

      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">Primary engine</div>
      <div className="flex flex-col sm:flex-row gap-3 mb-2">
        <EngineBtn id="gemini" title="Google Gemini" sub={noGemini ? "No API key configured on server" : "Your own key · no Universal credits used"} disabled={noGemini} />
        <EngineBtn id="emergent" title="Universal Key" sub="Emergent-managed · uses credits" />
      </div>
      {noGemini && <p className="text-[11px] text-amber-600 mb-2">Add a GEMINI_API_KEY on the server to enable Gemini as the primary engine.</p>}

      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mt-4 mb-2">Gemini model {engine === "emergent" && <span className="normal-case font-normal">(used when Gemini is the backup)</span>}</div>
      <select
        data-testid="scan-engine-gemini-model"
        value={model}
        onChange={(e) => setModel(e.target.value)}
        disabled={noGemini}
        className="w-full rounded-xl border border-riq-border bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-riq-primary/30 disabled:opacity-50"
      >
        {(ocr.gemini_models || []).map((m) => <option key={m.id} value={m.id}>{m.label} — {m.tag}</option>)}
      </select>
      {(() => {
        const sel = (ocr.gemini_models || []).find((m) => m.id === model);
        if (!sel) return null;
        return (
          <div data-testid="scan-engine-model-hint" className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 font-medium">⚡ {sel.speed}</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 font-medium">
              {"$".repeat(sel.cost_tier || 1)}<span className="text-amber-300">{"$".repeat(3 - (sel.cost_tier || 1))}</span> {sel.cost}
            </span>
            <span className="text-muted-foreground">Gemini is free within Google's limits — hints are relative trade-offs.</span>
          </div>
        );
      })()}

      <div className="mt-4 flex items-center justify-end">
        <button
          data-testid="scan-engine-save"
          disabled={!dirty || saving}
          onClick={save}
          className="inline-flex items-center gap-2 rounded-full bg-riq-primary text-white px-5 py-2.5 text-sm font-medium disabled:opacity-40 hover:-translate-y-0.5 transition-transform"
        >
          {saving ? <CircleNotch size={16} className="animate-spin" /> : <Check size={16} weight="bold" />} Save changes
        </button>
      </div>
    </Card>
  );
}

function UsageCard({ usage }) {
  const feats = usage.by_feature || [];
  const max = feats.length ? Math.max(...feats.map((f) => f.calls)) : 1;
  return (
    <Card data-testid="admin-llm-usage">
      <div className="flex items-center gap-2 mb-1"><Gauge size={20} weight="duotone" className="text-riq-primary" /><h3 className="font-head font-semibold text-riq-primary">Universal Key usage</h3></div>
      <p className="text-sm text-muted-foreground mb-4">How many AI (Universal Key) calls each feature makes — so you can see where credits go.</p>
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[["Total calls", usage.total], ["Last 7 days", usage.last_7d], ["Last 30 days", usage.last_30d]].map(([l, v]) => (
          <div key={l} className="rounded-xl bg-secondary/60 px-3 py-2.5 text-center">
            <div className="font-head text-xl font-bold text-riq-primary">{(v ?? 0).toLocaleString()}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{l}</div>
          </div>
        ))}
      </div>
      {feats.length === 0 ? (
        <p className="text-sm text-muted-foreground">No AI calls recorded yet.</p>
      ) : (
        <div className="space-y-2.5">
          {feats.map((f) => (
            <div key={f.feature} data-testid={`usage-feature-${f.feature}`} className="flex items-center gap-3">
              <span className="text-xs font-medium w-44 truncate shrink-0">{f.label}</span>
              <div className="flex-1 h-2.5 rounded-full bg-secondary overflow-hidden">
                <div className="h-full rounded-full bg-riq-primary" style={{ width: `${Math.max(5, (f.calls / max) * 100)}%` }} />
              </div>
              <span className="text-sm font-head font-bold text-riq-primary w-12 text-right shrink-0">{f.calls.toLocaleString()}</span>
              <span className="text-[11px] text-muted-foreground w-16 text-right shrink-0 hidden sm:block">~{f.avg_ms}ms{f.errors ? ` · ${f.errors} err` : ""}</span>
            </div>
          ))}
        </div>
      )}
      {usage.by_model?.length > 0 && (
        <div className="mt-4 pt-3 border-t border-riq-border flex flex-wrap gap-2">
          {usage.by_model.map((m) => (
            <span key={`${m.provider}/${m.model}`} className="text-[11px] rounded-full bg-secondary px-2.5 py-1 text-neutral-600">
              <b className="text-riq-primary">{m.provider}/{m.model}</b> · {m.calls.toLocaleString()}
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}

function AIChart({ chart, idx }) {
  return (
    <div data-testid={`admin-ai-chart-${idx}`} className="mb-2 rounded-2xl border border-riq-border bg-white p-4">
      {chart.title && <div className="text-sm font-head font-semibold text-riq-primary mb-3">{chart.title}</div>}
      <ResponsiveContainer width="100%" height={Math.max(180, chart.data.length * 34)}>
        <BarChart data={chart.data} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
          <CartesianGrid horizontal={false} stroke="#eef0f7" />
          <XAxis type="number" tick={{ fontSize: 11, fill: "#9aa0b4" }} axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="label" width={110} tick={{ fontSize: 11, fill: "#4a5568" }} axisLine={false} tickLine={false} />
          <Tooltip cursor={{ fill: "#f6f7fb" }} formatter={(v) => v.toLocaleString()} />
          <Bar dataKey="value" fill="#6D5AE6" radius={[0, 6, 6, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
